import type { Entity } from '@api/api/entity-schema';
import { InsightDrawerAssetHeaderContent } from '@terminal/components/insight-drawer-header/InsightDrawerAssetHeaderContent';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useKgInsight } from '@terminal/react-query-hooks/insights/useKgInsight';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import type { FC } from 'react';

import { CatalystWidgetView } from './CatalystWidgetView';
import { CatalystWidgetInsightContent } from './components/catalyst-widget-insight-content/CatalystWidgetInsightContent';
import { KgInsightSkeleton } from './components/kg-insight-content/KgInsightSkeleton';
import {
  getCatalystTrackingSource,
  resolveKgInsightChartHeaderConfig,
} from './utils/catalyst-widget-utils';

interface CatalystWidgetProps {
  entity?: Entity;
  navigateToKGInsight: () => void;
}

export const CatalystWidget: FC<CatalystWidgetProps> = ({
  entity,
  navigateToKGInsight,
}) => {
  const { params } = useQueryParams();
  const isInsightDrawerHeaderEnabled = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-4942-insight-drawer-header')
  );
  const insightId = params[queryKeys.insightId] ?? '';

  const { data: kgInsight, isLoading } = useKgInsight(insightId);

  if (isLoading) {
    return <KgInsightSkeleton />;
  }

  if (!kgInsight) {
    return null;
  }

  const shouldShowDrawerHeader =
    isInsightDrawerHeaderEnabled &&
    kgInsight.category === 'company_catalyst' &&
    kgInsight.subject.type === 'entity';
  const subjectEntity =
    shouldShowDrawerHeader && entity?.tag === kgInsight.subject.id
      ? entity
      : undefined;
  const subjectHeader = subjectEntity ? (
    <InsightDrawerAssetHeaderContent
      entity={subjectEntity}
      source={POSTHOG_TRACKING_SOURCES.CATALYST_INSIGHT_DRAWER}
    />
  ) : undefined;
  const chartHeaderConfig = resolveKgInsightChartHeaderConfig({
    isRedesignEnabled: isInsightDrawerHeaderEnabled,
    kgInsight,
    trackingSource: getCatalystTrackingSource(kgInsight.category),
  });

  return (
    <CatalystWidgetView headerSlot={subjectHeader} subject={kgInsight.subject}>
      <CatalystWidgetInsightContent
        {...chartHeaderConfig}
        kgInsight={kgInsight}
        navigateToKGInsight={navigateToKGInsight}
        showDrawerHeader={shouldShowDrawerHeader}
      />
    </CatalystWidgetView>
  );
};
